import { ContentFactory } from './entity/contentFactory';
import { AlreadyRead } from './entity/valueObjects/alreadyRead';

import type {
  SentNewsEntity, SentNewsJsonPub, SentNewsJsonPvt, SentNewsPropsPub, SentNewsPropsPvt,
} from './entity/sentNewsEntity';
import type { SentNewsIRepository } from './entity/sentNewsIRepository';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';

import { DomainServiceBase } from '@/domain/domainServiceBase';

/**
 * お知らせドメインサービス
 */
export class NewsDomainService extends DomainServiceBase {
  private wagakoNurseryApi!: WagakoNurseryIApi;

  private sentNewsRepository!: SentNewsIRepository;

  private contentFactory = new ContentFactory();

  /**
   * Injection
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param sentNewsRepository sentNewsRepository
   */
  inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    sentNewsRepository: SentNewsIRepository,
  ) {
    this.wagakoNurseryApi = wagakoNurseryApi;
    this.sentNewsRepository = sentNewsRepository;
  }

  /**
   * お知らせ一覧取得
   * @param token トークン
   * @returns 結果
   */
  async getSentNewsList(token: string | null) {
    const result = await this.sentNewsRepository.getList(token);
    const { nextToken } = result;
    const items = result.items.map((v: SentNewsEntity) => v.toJson());
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items,
    };
  }

  /**
   * 既読
   * @param entity Entity
   */
  async alreadyReadNews(
    entity: SentNewsEntity,
  ) {
    entity.setAlreadyRead(AlreadyRead.create(true));
    await this.wagakoNurseryApi.reply<
      SentNewsPropsPvt,
      SentNewsJsonPvt,
      SentNewsPropsPub,
      SentNewsJsonPub
    >(entity);
  }

  /**
   * コンテンツを読み込む
   * @param item アイテム
   * @returns 結果
   */
  async loadContent(item: SentNewsEntity) {
    const { data } = await this.wagakoNurseryApi.loadFileFromS3(
      item.s3Path.value,
      item.fromId.value,
    );
    const text = this.wagakoNurseryApi.gunzipText(data);
    const ent = this.contentFactory.createFromJsonPvt(JSON.parse(text));
    return {
      /** コンテンツ */
      content: ent,
      /** インフォ */
      info: item,
    };
  }
}
